import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Clock3,
  CalendarDays,
  MoreVertical,
  Video,
  Phone,
  Headphones,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Link as LinkIcon,
  User,
  Mail,
  Phone as PhoneIcon,
  MapPin,
} from "lucide-react";
import { getAppointments } from "../../api/api";
import AppoinmentModal from "../AppoinmnetModal";

const MyAppointments = () => {
  const [activeTab, setActiveTab] = useState("Upcoming");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [allAppointments, setAllAppointments] = useState([]);
  const [imageErrors, setImageErrors] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchAllAppointments = async () => {
    try {
      setLoading(true);
      const response = await getAppointments(1, 100);
      if (response && response.status === true) {
        let allData = [...(response.data || [])];
        const totalPages = response.totalPages || 1;
        if (totalPages > 1) {
          const promises = [];
          for (let page = 2; page <= totalPages; page++) {
            promises.push(getAppointments(page, 100));
          }
          const remainingResponses = await Promise.all(promises);
          remainingResponses.forEach((res) => {
            if (res && res.status === true && res.data) {
              allData = [...allData, ...res.data];
            }
          });
        }
        setAllAppointments(allData);
      } else {
        console.error(response?.message || "Failed to load appointments");

        setAllAppointments([]);
      }
    } catch (error) {
      console.error("Fetch appointments error:", error);

      setAllAppointments([]);
    } finally {
      setLoading(false);

      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllAppointments();
  }, []);

  const onRefresh = useCallback(() => {
    setRefreshing(true);

    fetchAllAppointments();
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    fetchAllAppointments();
  };

  const handleStartCall = (appointment, e) => {
    e?.stopPropagation();
    const mode = appointment.mode_consultation?.toLowerCase() || "";
    if (mode.includes("video")) {
      if (!appointment.video_link) {
        alert("Video call link not available yet");

        return;
      }
      window.open(appointment.video_link, "_blank");
    } else if (mode.includes("voice") || mode.includes("audio")) {
      if (!appointment.phone_number) {
        alert("Audio call number not available yet");
        return;
      }
      window.location.href = `tel:${appointment.phone_number}`;
    } else {
      alert("Consultation type not supported");
    }
  };

  const handleNeedSupport = (appointment, e) => {
    e?.stopPropagation();
    console.log("Navigate to help support");
  };

  const handleViewDetails = (appointment) => {
    console.log("View details for appointment:", appointment);
  };

  const isCallEnabled = (appointment) => {
    const mode = appointment.mode_consultation?.toLowerCase() || "";
    if (mode.includes("video")) {
      return appointment.video_link && appointment.video_link.trim() !== "";
    } else if (mode.includes("voice") || mode.includes("audio")) {
      return appointment.phone_number && appointment.phone_number.trim() !== "";
    }
    return false;
  };

  const getInitials = (name) => {
    if (!name) return "?";

    return name.charAt(0).toUpperCase();
  };

  const formatName = (name) => {
    if (!name) return "Patient";
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getRandomColor = (name) => {
    const colors = [
      "#18B58A",
      "#3A7AFE",
      "#FF8C00",
      "#FF4444",

      "#9C27B0",
      "#E91E63",
      "#00BCD4",
      "#4CAF50",

      "#FF9800",
      "#795548",
      "#607D8B",
      "#9E9E9E",
    ];

    if (!name) return colors[0];
    const index =
      name.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
      colors.length;

    return colors[index];
  };

  const getFilteredAppointments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (activeTab) {
      case "Upcoming":
        return allAppointments.filter((a) => {
          const isUpcomingStatus = a.status === 0 || a.status === 1;
          if (a.appointment_date) {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isTodayOrFuture = appointmentDate >= today;
            return isUpcomingStatus && isTodayOrFuture;
          }
          return isUpcomingStatus;
        });

      case "Completed":
        return allAppointments.filter((a) => {
          const isCompletedStatus = a.status === 2;
          if (a.appointment_date) {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isPast = appointmentDate < today;
            return isCompletedStatus || isPast;
          }
          return isCompletedStatus;
        });
      case "Cancelled":
        return allAppointments.filter((a) => a.status === 3);
      default:
        return [];
    }
  };

  const getFollowupStatusDisplay = (followupStatus) => {
    const statusMap = {
      pending: { text: "Follow-up Pending", color: "#FF8C00", icon: Clock },
      completed: {
        text: "Follow-up Completed",
        color: "#18B58A",
        icon: CheckCircle,
      },

      scheduled: {
        text: "Follow-up Scheduled",
        color: "#3A7AFE",
        icon: Calendar,
      },

      cancelled: {
        text: "Follow-up Cancelled",
        color: "#FF4444",
        icon: XCircle,
      },
    };

    return (
      statusMap[followupStatus?.toLowerCase()] || {
        text: "No Follow-up",
        color: "#999",
        icon: AlertCircle,
      }
    );
  };

  const getPaymentStatusDisplay = (paymentStatus) => {
    const statusMap = {
      pending: { text: "Payment Pending", color: "#FF8C00", icon: Clock },

      success: {
        text: "Payment Completed",
        color: "#18B58A",
        icon: CheckCircle,
      },

      failed: { text: "Payment Failed", color: "#FF4444", icon: XCircle },

      refunded: { text: "Payment Refunded", color: "#3A7AFE", icon: RefreshCw },
    };

    return (
      statusMap[paymentStatus?.toLowerCase()] || {
        text: "Payment Unknown",
        color: "#999",
        icon: AlertCircle,
      }
    );
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "TODAY";

    try {
      const date = new Date(dateString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (date.toDateString() === today.toDateString()) {
        return "TODAY";
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return "TOMORROW";
      } else {
        return date
          .toLocaleDateString("en-US", {
            day: "numeric",

            month: "short",

            year: "numeric",
          })
          .toUpperCase();
      }
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "10:00 AM";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getConsultationType = (appointment) => {
    const mode = appointment.mode_consultation?.toLowerCase() || "";

    if (mode.includes("video")) return "Video Consultation";

    if (mode.includes("voice") || mode.includes("audio"))
      return "Voice Consultation";
    return "Consultation";
  };

  const filteredAppointments = getFilteredAppointments();

  const groupedAppointments = filteredAppointments.reduce(
    (groups, appointment) => {
      const date = formatDisplayDate(appointment.appointment_date);
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(appointment);
      return groups;
    },
    {},
  );

  const sortedDates = Object.keys(groupedAppointments).sort((a, b) => {
    if (a === "TODAY") return -1;
    if (b === "TODAY") return 1;
    if (a === "TOMORROW") return -1;
    if (b === "TOMORROW") return 1;
    return new Date(a) - new Date(b);
  });

  const getTabCount = (tab) => {
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    switch (tab) {
      case "Upcoming":
        return allAppointments.filter((a) => {
          const isUpcomingStatus = a.status === 0 || a.status === 1;
          if (a.appointment_date) {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isTodayOrFuture = appointmentDate >= today;
            return isUpcomingStatus && isTodayOrFuture;
          }
          return isUpcomingStatus;
        }).length;

      case "Completed":
        return allAppointments.filter((a) => {
          const isCompletedStatus = a.status === 2;

          if (a.appointment_date) {
            const appointmentDate = new Date(a.appointment_date);
            appointmentDate.setHours(0, 0, 0, 0);
            const isPast = appointmentDate < today;
            return isCompletedStatus || isPast;
          }
          return isCompletedStatus;
        }).length;
      case "Cancelled":
        return allAppointments.filter((a) => a.status === 3).length;
      default:
        return 0;
    }
  };

  if (loading) {
    return (
      <div className="w-full font-lato">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-[34px] font-semibold text-[#22302b] leading-tight">
              My Appointments
            </h2>

            <p className="text-[14px] text-[#9aa39d] mt-2">
              Manage your upcoming consultations and review past sessions.
            </p>
          </div>
        </div>

        <div className="flex gap-8 border-b border-[#e8ece8] mb-7">
          {["Upcoming", "Completed", "Cancelled"].map((tab) => (
            <button
              key={tab}
              className="text-[14px] font-medium pb-3 text-[#a0a8a2]"
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#57b26a]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full font-lato">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div>
          <h2 className="text-[34px] font-semibold text-[#22302b] leading-tight">
            My Appointments
          </h2>
          <p className="text-[14px] text-[#9aa39d] mt-2">
            Manage your upcoming consultations and review past sessions.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            className="h-[42px] px-5 rounded-md bg-[#57b26a] hover:bg-[#49aa5d] text-white text-[13px] font-medium flex items-center gap-2"
            onClick={handleOpenModal}  
          >
            <Plus size={15} />
            Book New Session
          </button>
        </div>
      </div>

      <div className="flex gap-8 border-b border-[#e8ece8] mb-7 overflow-x-auto">
        {["Upcoming", "Completed", "Cancelled"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`text-[14px] font-medium pb-3 whitespace-nowrap transition-all ${activeTab === tab
              ? "text-[#22302b] border-b-2 border-[#56b96a]"
              : "text-[#a0a8a2] hover:text-[#22302b]"
              }`}
          >
            {tab} ({getTabCount(tab)})
          </button>
        ))}
      </div>

      {filteredAppointments.length === 0 ? (
        <div className="bg-white border border-[#e8ece8] rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-[#f7f9f7] rounded-full flex items-center justify-center mx-auto mb-4">
            <CalendarDays size={24} className="text-[#9aa39d]" />
          </div>

          <h3 className="text-[18px] font-semibold text-[#22302b] mb-2">
            No {activeTab.toLowerCase()} appointments
          </h3>
          <p className="text-[14px] text-[#9aa39d] mb-4">
            {activeTab === "Upcoming"
              ? "You don't have any upcoming appointments scheduled."
              : activeTab === "Completed"
                ? "You haven't completed any sessions yet."
                : "No canceled sessions to show."}
          </p>

          {activeTab === "Upcoming" && (
            <button className="h-[42px] px-5 rounded-md bg-[#57b26a] hover:bg-[#49aa5d] text-white text-[13px] font-medium flex items-center gap-2 mx-auto"             onClick={handleOpenModal}  
>
              <Plus size={15} />
              Book New Session
            </button>
          )}
        </div>

      ) : (
        <>
          {sortedDates.map((date) => (
            <div key={date} className="mb-8">
              <h3 className="text-[12px] font-semibold text-[#999] mb-3 uppercase tracking-wide">
                {date}
              </h3>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                {groupedAppointments[date].map((appointment) => {
                  const callEnabled = isCallEnabled(appointment);

                  const followupDisplay = getFollowupStatusDisplay(
                    appointment.followup_status,
                  );

                  const PaymentIcon = followupDisplay.icon;

                  const paymentDisplay = getPaymentStatusDisplay(
                    appointment.payment_status,
                  );

                  const PayIcon = paymentDisplay.icon;

                  const name = appointment.name || "Patient";

                  const formattedName = formatName(name);

                  const initials = getInitials(name);

                  const backgroundColor = getRandomColor(name);

                  const consultationType = getConsultationType(appointment);

                  const isVideoCall = appointment.mode_consultation
                    ?.toLowerCase()
                    .includes("video");

                  return (
                    <div
                      key={appointment.id}
                      className="bg-white border border-[#e8ece8] rounded-xl p-5 hover:shadow-lg transition-shadow cursor-pointer"
                      onClick={() => handleViewDetails(appointment)}
                    >
                      <div className="flex items-start justify-between mb-5">
                        <span className="text-[11px] px-3 py-1 rounded-full font-medium bg-[#e9f7f2] text-[#18b58a]">
                          {formatTime(appointment.appointment_time)}
                        </span>

                        <MoreVertical
                          size={16}
                          className="text-[#9aa39d] cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <div className="flex items-center gap-4 mb-5">
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg"
                          style={{ backgroundColor }}
                        >
                          {initials}
                        </div>

                        <div className="flex-1">
                          <h4 className="text-[18px] font-semibold text-[#22302b]">
                            {formattedName}
                          </h4>

                          <p className="text-[13px] text-[#9aa39d] mt-1">
                            {appointment.purpose || "Consultation"}
                          </p>

                          {appointment.comments && (
                            <p className="text-[12px] text-[#bbb] mt-1 truncate">
                              {appointment.comments}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        {isVideoCall ? (
                          <Video size={16} className="text-[#57b96b]" />
                        ) : (
                          <Phone size={16} className="text-[#57b96b]" />
                        )}

                        <span className="text-[13px] text-[#555]">
                          {consultationType} · {appointment.age || "30"} mins
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <div
                          className="flex items-center gap-1.5 px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: followupDisplay.color + "20",
                          }}
                        >
                          <PaymentIcon
                            size={12}
                            style={{ color: followupDisplay.color }}
                          />

                          <span
                            className="text-[10px] font-medium"
                            style={{ color: followupDisplay.color }}
                          >
                            {followupDisplay.text}
                          </span>
                        </div>

                        <div
                          className="flex items-center gap-1.5 px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: paymentDisplay.color + "20",
                          }}
                        >
                          <PayIcon
                            size={12}
                            style={{ color: paymentDisplay.color }}
                          />

                          <span
                            className="text-[10px] font-medium"
                            style={{ color: paymentDisplay.color }}
                          >
                            {paymentDisplay.text}
                          </span>
                        </div>
                      </div>

                      {(appointment.video_link || appointment.phone_number) && (
                        <div className="flex items-center gap-2 mb-4 bg-[#f0f9f4] p-2 rounded-md">
                          <LinkIcon size={14} className="text-[#18b58a]" />

                          <span className="text-[11px] text-[#18b58a] truncate flex-1">
                            {appointment.video_link || appointment.phone_number}
                          </span>
                        </div>
                      )}

                      {(activeTab === "Upcoming" ||
                        appointment.status === 0 ||
                        appointment.status === 1) && (
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              className={`h-[42px] rounded-md text-[13px] font-medium flex items-center justify-center gap-2 ${callEnabled
                                ? "bg-[#57b26a] hover:bg-[#49aa5d] text-white"
                                : "bg-[#f2f4f2] text-[#b4bbb6] cursor-not-allowed"
                                }`}
                              onClick={(e) => {
                                e.stopPropagation();

                                handleStartCall(appointment, e);
                              }}
                              disabled={!callEnabled}
                            >
                              {isVideoCall ? (
                                <Video size={14} />
                              ) : (
                                <Phone size={14} />
                              )}
                              Join Call
                            </button>

                            <button
                              className="h-[42px] rounded-md border border-[#18b58a] bg-[#f0f9f4] text-[#18b58a] text-[13px] font-medium flex items-center justify-center gap-2 hover:bg-[#e6f5ef]"
                              onClick={(e) => handleNeedSupport(appointment, e)}
                            >
                              <Headphones size={14} />
                              Need Support
                            </button>
                          </div>
                        )}

                      {(activeTab === "Completed" ||
                        activeTab === "Cancelled" ||
                        appointment.status === 2 ||
                        appointment.status === 3) && (
                          <button
                            className="w-full h-[42px] mt-2 rounded-md border border-[#18b58a] text-[#18b58a] text-[13px] font-medium hover:bg-[#f0f9f4] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();

                              handleViewDetails(appointment);
                            }}
                          >
                            View Details
                          </button>
                        )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
      {activeTab === "Upcoming" &&
        (() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          const pastAppointments = allAppointments.filter((item) => {
            if (!item.appointment_date) return false;

            const oldDate = new Date(item.appointment_date);
            oldDate.setHours(0, 0, 0, 0);

            return oldDate < today;
          });

          if (pastAppointments.length === 0) return null;

          return (
            <div className="mt-10">
              <h3 className="text-[22px] font-semibold text-[#22302b] mb-4">
                Past Sessions
              </h3>

              <div className="overflow-x-auto bg-white border border-[#e8ece8] rounded-xl">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-[#edf1ed] bg-[#fafcfa]">
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Date & Time
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Session Type
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Specialist
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Status
                      </th>
                      <th className="text-left px-5 py-4 text-[13px] text-[#98a19a] font-medium">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {pastAppointments.map((item) => {
                      const dateObj = new Date(item.appointment_date);

                      const showDate = dateObj.toLocaleDateString("en-US", {
                        month: "short",
                        day: "2-digit",
                        year: "numeric",
                      });

                      return (
                        <tr
                          key={item.id}
                          className="border-b border-[#f3f5f3] last:border-b-0"
                        >
                          <td className="px-5 py-4">
                            <p className="text-[14px] font-medium text-[#22302b]">
                              {showDate}
                            </p>
                            <p className="text-[12px] text-[#9aa39d] mt-1">
                              {formatTime(item.appointment_time)}
                            </p>
                          </td>

                          <td className="px-5 py-4 text-[14px] text-[#22302b]">
                            {getConsultationType(item)}
                          </td>

                          <td className="px-5 py-4 text-[14px] text-[#22302b]">
                            {item.name || "N/A"}
                          </td>

                          <td className="px-5 py-4">
                            <span
                              className={`px-3 py-1 rounded-full text-[12px] font-medium ${item.status === 2
                                ? "bg-[#e8f8ed] text-[#43a85c]"
                                : item.status === 3
                                  ? "bg-[#f4f4f4] text-[#9aa39d]"
                                  : "bg-[#fff6e5] text-[#d28a00]"
                                }`}
                            >
                              {item.status === 2
                                ? "Completed"
                                : item.status === 3
                                  ? "Canceled"
                                  : "Completed"}
                            </span>
                          </td>

                          <td className="px-5 py-4">
                            <button
                              onClick={() => handleViewDetails(item)}
                              className={`h-[34px] px-4 rounded-md text-[12px] font-medium border ${item.status === 3
                                ? "border-[#57b26a] text-[#57b26a]"
                                : "border-[#dfe5df] text-[#57b26a]"
                                }`}
                            >
                              {item.status === 3 ? "Rebook" : "View Notes"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
      {isModalOpen && (
        <AppoinmentModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default MyAppointments;
